import { defineComponent, h, ref, computed, onBeforeMount } from 'vue';
import { modalProps, ModalProps } from '@/ui/components/Modal';
import { t } from '@/ui/i18n/i18n';
import { TodoIssueRecord } from '@/entities/issue';
import { IssueListProps, issuesList } from '@/ui/components/issue/ManageIssuesModal/IssuesListModal';
import { EventBus } from '@/ui/EventBus';
import { Events } from '@/ui/events';
import { toasted } from '@/ui/helpers';
import { editIssue } from '@/ui/components/issue/ManageIssuesModal/EditIssueModal';
import { createIssueModal } from '@/ui/components/issue/ManageIssuesModal/CreateIssueModal';
import { IssueRecordEditable } from '@/ui/pages/issue/issueTypes';
import { VNodeData } from 'vue/types/vnode.d';
import { IssueRecord } from '@/entities/issue/v2';

enum ModalViewingState {
  CREATE_ISSUE,
  LISTING_ISSUE,
  UPDATING_ISSUE,
}

interface Props extends ModalProps {
  issues: IssueRecord[];
  mergeAttributes?: Partial<IssueRecordEditable>;
  issueListProps?: Partial<IssueListProps>;
}

export const Index = defineComponent<Props>({
  name: 'ManageIssuesModal',
  props: [...modalProps, 'issues', 'mergeAttributes', 'issueListProps'],
  setup(props, context) {
    const modalState = ref<ModalViewingState>(ModalViewingState.CREATE_ISSUE);

    const closeModal = () => {
      context.emit('close');
    };

    const issueSelected = ref<null | TodoIssueRecord>(null);

    const loading = ref(false);

    const onCreatedIssue = () => {
      loading.value = false;
      toasted.success(t('issues.created'));
      context.emit('issue:created');
      modalState.value = ModalViewingState.LISTING_ISSUE;
    };

    const isUpdatingIssue = computed(
      () => issueSelected.value !== null && modalState.value === ModalViewingState.UPDATING_ISSUE,
    );

    const showIssueList = computed(() => modalState.value === ModalViewingState.LISTING_ISSUE);

    const hasIssues = computed(() => props.issues.length > 0);

    const setIssueSelected = (issue: TodoIssueRecord | null) => {
      issueSelected.value = issue;
      modalState.value = ModalViewingState.UPDATING_ISSUE;
    };

    const onIssueUpdated = () => {
      loading.value = false;
      toasted.success(t('issues.updated'));
      issueSelected.value = null;
      modalState.value = ModalViewingState.LISTING_ISSUE;
      context.emit('issue:updated');
    };

    const onIssueCreateClick = () => {
      loading.value = true;
      EventBus.$emit(Events.submitCreateIssue);
      modalState.value = ModalViewingState.CREATE_ISSUE;
    };

    const onIssueUpdateClick = () => {
      loading.value = true;
      EventBus.$emit(Events.updateIssue);
    };

    const onCreateIssueClick = () => {
      modalState.value = ModalViewingState.CREATE_ISSUE;
    };

    const decideModalState = () => {
      modalState.value = hasIssues.value
        ? ModalViewingState.LISTING_ISSUE
        : ModalViewingState.CREATE_ISSUE;
    };

    const onCloseModalFromSubModal = () => {
      if (hasIssues.value) {
        modalState.value = ModalViewingState.LISTING_ISSUE;
      } else {
        closeModal();
      }
    };

    onBeforeMount(decideModalState);

    return function render() {
      if (!props.active) {
        return null;
      }

      if (showIssueList.value) {
        let componentProps = { issues: props.issues };

        if (props.issueListProps) {
          componentProps = { ...componentProps, ...props.issueListProps };
        }

        return issuesList({
          props: componentProps,
          on: {
            'selected:issue': setIssueSelected,
            'create:issue:click': onCreateIssueClick,
            close: closeModal,
          },
        });
      }

      if (isUpdatingIssue.value) {
        return editIssue({
          props: { issue: issueSelected.value!, loading: loading.value },
          on: {
            close: onCloseModalFromSubModal,
            'updated:issue': onIssueUpdated,
            'update:click:issue': onIssueUpdateClick,
          },
        });
      }

      return createIssueModal({
        props: {
          mergeAttributes: props.mergeAttributes,
          loading: loading.value,
        },
        on: {
          'issue:click:create': onIssueCreateClick,
          'created:issue': onCreatedIssue,
          close: closeModal,
        },
      });
    };
  },
});

export function manageIssueModal(data: VNodeData & { props: Props }) {
  return h(Index, data);
}
